import { DataNode } from 'antd/es/tree';
import { t } from 'i18next';
import { UserRoleDataType } from 'src/app/services/user-role/user-role-service.type';
import { permissionsConfig } from 'src/configs';
import { PermissionOptions } from 'src/configs/permissions.config';
import { LAYOUTVIEW } from 'src/configs/system.config';

export interface CustomDataNode extends DataNode {
  isAtomic?: boolean;
  isView?: boolean;
}

/*
This function iterates through all the tree and calculate every permission in that tree to decide whether that user has it or not.
Also it checkes view keys for that permission and return these two results
*/
export const generateDefaultPermissionsTree = (permissions: string[]) => {
  const tree: { [key: string]: CustomDataNode[] } = {};
  const viewKeys: { key: string; checked: boolean }[] = [];

  Object.entries(permissionsConfig.options).forEach((routes) => {
    tree[t(routes[1].key)] = [];
  });

  const hyphenate = (s: string) => s.match(/.{1,2}/g)!.join('-') ?? s;
  const isExcluded = (node: any) => Array.isArray(node?.excludeLayouts) && node.excludeLayouts.includes(LAYOUTVIEW());

  const traverse = (node: any, accCode: string, routeTitle: string) => {
    if (isExcluded(node)) return;

    const code = `${accCode}${node.value}`;

    const hasValidDirectChild = !!node?.options && Object.values(node.options).some((child: any) => !isExcluded(child));

    if (hasValidDirectChild) {
      viewKeys.push({ key: code, checked: permissions.includes(code) });
    }

    if (permissions.includes(code)) {
      tree[routeTitle].push({
        key: hyphenate(code),
        isAtomic: !hasValidDirectChild,
        isView: hasValidDirectChild,
      });
    }

    if (node?.options) {
      Object.values(node.options).forEach((child: any) => {
        traverse(child, code, routeTitle);
      });
    }
  };

  Object.entries(permissionsConfig.options).forEach(([, routeNode]) => {
    traverse(routeNode, '', t(routeNode.key));
  });

  return { tree, viewKeys };
};

export const getRouteFromPermission = (permission: string) => {
  const foundRoute = Object.entries(permissionsConfig.options).find(
    ([_key, value]) => value.isLanding && value.value === permission,
  );
  if (!foundRoute) return 'Login';
  return foundRoute[0];
};

// This function returns permission key string that is defined in permission.config.ts from given Route array.
export const getPermissionKey = (arr: [string, ...string[]]): string => {
  let currentRole: any = permissionsConfig.options[arr[0]];
  let totalValue: string = '';
  for (let i = 1; i <= arr.length; i += 1) {
    totalValue += currentRole.value;

    if (i >= arr.length) break;
    currentRole = currentRole?.options[arr[i]];
  }
  return totalValue;
};

// This functions decides given user permissions include necessary permission or not
export const checkPermission = (userPermissions: string[], arr: [string, ...string[]]) => {
  // @ts-ignore

  let currentRole: any = permissionsConfig.options[arr[0]];
  let totalValue: string = '';
  for (let i = 1; i <= arr.length; i += 1) {
    totalValue += currentRole.value;
    if (!hasPart(userPermissions, totalValue, i * 2)) {
      return false;
    }
    if (i >= arr.length) break;
    currentRole = currentRole?.options[arr[i]];
  }
  return true;
};

const hasPart = (arr: string[], value: string, index: any): boolean => {
  return arr.some((obj) => {
    return obj.substring(0, index) === value;
  });
};

// This is helper function to iterate permission tree recursively
const preOrderTraversal = (obj: any, rootKey: string): CustomDataNode | undefined => {
  if (obj[1].excludeLayouts) {
    if (!genericExcludeFilter(obj[1].excludeLayouts, LAYOUTVIEW())) return;
  }

  const totalRootKey = `${rootKey}-${obj[1].value}`;

  const children: CustomDataNode[] = [];

  if (obj[1].options) {
    Object.entries(obj[1].options).forEach((item) => {
      const node = preOrderTraversal(item, totalRootKey);
      if (node) children.push(node);
    });
  }

  const isView = children.length > 0;

  if (!isView) {
    return {
      title: t(obj[1].key),
      key: totalRootKey,
      isAtomic: true,
    };
  }

  return {
    title: t(obj[1].key),
    key: `${totalRootKey}`,
    children,
    isView,
  };
};

/* 
This function builds tree for given route by checking every permission for that route in permission.config.ts recursively
*/
export const generatePermissionsTree = (route: [string, PermissionOptions]): CustomDataNode | undefined => {
  const root = permissionsConfig.options[route[0]];

  if (root.excludeLayouts) {
    if (!genericExcludeFilter(root.excludeLayouts, LAYOUTVIEW())) return;
  }

  const children: CustomDataNode[] = [];

  if (root.options) {
    Object.entries(root.options).forEach((item) => {
      if (item[1].options) {
        const node = preOrderTraversal(item, root.value);
        if (node) children.push(node);
      } else {
        if (item[1].excludeLayouts && !genericExcludeFilter(item[1].excludeLayouts, LAYOUTVIEW())) {
          return;
        }

        children.push({
          title: t(item[1].key),
          key: `${root.value}-${item[1].value}`,
          children: [],
          isAtomic: true,
        });
      }
    });
  }

  const isView = children.length > 0;

  if (!isView) {
    return {
      title: t(route[1].key),
      key: root.value,
      isAtomic: true,
    };
  }

  return {
    title: t(route[1].key),
    key: `${root.value}`,
    children,
    isView,
  };
};

export const genericExcludeFilter = <T>(excludeList: T[], value: T) => {
  if (excludeList.includes(value)) {
    return false;
  }
  return true;
};

export const getLandingPage = (userRole: UserRoleDataType[]) => {
  let route = '';
  userRole.forEach((role) => {
    for (const per of role.permissions) {
      if (per.permissionId.length > 2) {
        continue;
      }
      route = getRouteFromPermission(per.permissionId);
      if (route === '/') break;
    }
  });

  return route || 'NoPermissionLanding';
};
